import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../environments/environment';
import { createHttpParams } from 'src/services/utilities';
import { LocalStorageService } from './local-storage.service';
import { ApiRequestParams } from 'src/models/global-interfaces';
import {
  UserLoginRequest,
  LoginResponse,
  RefreshTokenResource,
  UserResource,
  UserWithPermissionsResource,
  userTableApiRequestParams,
  TableUser,
  CreateUserRequest,
  UpdateUserRequest,
  ChangePasswordRequest,
  UserInfoResource,
  CustomerResource,
} from 'src/models/user.model';
import { components } from 'be-types';
import { JwtAuthService } from './jwt-auth.service';

export type NotificationResponse = components['schemas']['UserProfileSettingsResource'];

@Injectable()
export class UserService {
  private apiUrl = environment.apiUrl;
  private readonly http = inject(HttpClient);
  private readonly authService = inject(JwtAuthService);
  private readonly ls = inject(LocalStorageService);

  login = (params: UserLoginRequest) => {
    this.ls.clearAuth();
    return this.http.post<LoginResponse>(`${this.apiUrl}auth/login`, params).pipe(
      tap(res => {
        if (res) {
          this.authService.setUserToken(res.access_token);
          this.authService.setRefreshToken(res.refresh_token);
        }
      }),
    );
  };

  refreshToken(refreshToken: string): Observable<RefreshTokenResource> {
    return this.http
      .post<RefreshTokenResource>(`${this.apiUrl}auth/refresh-token`, {
        refreshToken: refreshToken,
      })
      .pipe(
        tap(res => {
          this.authService.setUserToken(res.access_token);
          this.authService.setRefreshToken(res.refresh_token);
        }),
      );
  }

  getCurrentUser = () => {
    return this.http.get<UserResource>(`${this.apiUrl}users/info`);
  };

  getUserById = (id: number, locationID: number) =>
    this.http.get<UserWithPermissionsResource>(
      `${this.apiUrl}users/${id}${locationID ? `?locationID=${locationID}` : ''}`,
    );

  getUsers = (params: ApiRequestParams = userTableApiRequestParams, locationID?: number) => {
    if (locationID) params.locationID = locationID;
    return this.http.get<TableUser[]>(`${this.apiUrl}users`, {
      params: createHttpParams(params),
    });
  };

  createUser = (params: CreateUserRequest) =>
    this.http.post<UserInfoResource>(`${this.apiUrl}users`, { ...params });

  updateUser = (userID: number, user: UpdateUserRequest) =>
    this.http.put<UserInfoResource>(`${this.apiUrl}users/${userID}`, user);

  validateUsername = (userLoginName: string) =>
    this.http
      .post<{ usernameExists: boolean }>(`${this.apiUrl}check-username`, { userLoginName })
      .pipe(map(res => res.usernameExists));

  validateEmail = (userEmail: string) =>
    this.http
      .post<{ userEmailExists: boolean }>(`${this.apiUrl}check-user-email`, { userEmail })
      .pipe(map(res => res.userEmailExists));

  changePassword = (payload: ChangePasswordRequest) =>
    this.http.post<UserInfoResource>(`${this.apiUrl}users/change-password`, payload);

  updateNotificationSettings = (payload: NotificationResponse) =>
    this.http.put<NotificationResponse>(`${this.apiUrl}users/profile-settings`, {
      ...payload,
    });

  getNotificationSettings = () =>
    this.http.get<NotificationResponse>(`${this.apiUrl}users/profile-settings`);

  resetPassword = (userId: number) =>
    this.http.put<NotificationResponse>(`${this.apiUrl}users/reset-password/${userId}`, {});

  getCustomerInfo = () => this.http.get<CustomerResource>(`${this.apiUrl}customer`);

  forgotPassword = (email: string) =>
    this.http.post<unknown>(`${this.apiUrl}auth/forgot-password`, { email });

  resetPasswordThroughToken = (password: string, token: string) =>
    this.http.post<UserInfoResource>(
      `${this.apiUrl}auth/reset-password`,
      {
        password,
        token,
      },
      { observe: 'response' },
    );
}
